import React, {
    useState,
    useEffect,
    useRef,
    useImperativeHandle,
    forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import { Controlled as CodeMirror } from 'react-codemirror2';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep,
} from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import Checkbox from '@mui/material/Checkbox';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';

import Filter from './filter';
import Validator, { useValidate, checkValidity, typeChecks } from '../validator';

import SystemService from '../../data/system-service';

const useStyles = makeStyles(theme => ({
    fullWidth: {
        width: '100%',
    },
    timezone: {
        width: '120px',
    },
    hideFileInput: {
        display: 'none',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    certBox: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.primary.main}`,
        background: theme.styled.paper.background,
    },
    codemirrorBox: {
        display: 'block',
        width: '100%',
    },
    codeContainer: {
        height: '120px',
        marginBottom: '-8px',
        '& .react-codemirror2': {
            height: '100%',
        },
        '& .CodeMirror': {
            height: '100%',
        }
    },
}));

const FORMAT_STRINGS = ['$proto$', '$src_ip$', '$src_port$', '$dst_ip$', '$dst_port$', '$sensor_uuid$', '$sensor_ipv4$', '$sensor_ipv6$', '$sensor_ip$',
    '$sensor_name$', '$sensor_fqdn$', '$timestamp$', '$timestamp_epoch_ms$', '$timestamp_epoch_fract$', '$severity$',
    '$severity_inv$', '$signature$', '$signature_id$', '$cmc_name$', '$cmc_fqdn$', '$cmc_ip$', '$nic$', '$category$', '$action$',
    '$alert_group$', '$signature_gid$', '$signature_rev$', '$sys_version$',
    '$health_host$', '$health_message$', '$health_type$', '$health_state$',
    '$health_uuid$', '$health_check$', '$health_service$'];

const Syslog = forwardRef((props, ref) => {
    const classes = useStyles();
    const theme = useTheme();
    const [systemConstants, setSystemConstants] = useState({
        facilities: [],
        severities: [],
        timezones: [],
    });
    const filterRef = useRef(null);
    // const syslogRef = useRef(null);
    const [init, setInit] = useState(true);
    const [original, setOriginal] = useState(_cloneDeep(props.data));
    const [data, setData] = useState(_cloneDeep(props.data));
    const [, setChanges] = useState({});
    const dataForm = useValidate({});
    const [formatRef, setFormatRef] = useState(null);

    useImperativeHandle(ref, () => ({
        getData: () => {
            const f = filterRef.current.getFilter();
            return {
                ...data,
                filterconf: f,
            };
        }
    }));

    useEffect(() => {
        SystemService.getConstants().then((res) => {
            setSystemConstants(res);
        });
    }, []);

    useEffect(() => {
        const d = { ...props.data };
        if (d.prune_type === '') {
            d.prune_type = 'inactive';
        }

        if (init) {
            setOriginal(_cloneDeep(d));
            setData(_cloneDeep(d));
            setInit(false);
        }
    }, [init, props]);

    useEffect(() => {

        if (!init) {
            dataForm.set({
                host: typeChecks.host?.(data?.dest?.host ?? ''),
                port: typeChecks.port?.(data?.dest?.port ?? ''),
                range: data.mark.enable
                ? typeChecks.range?.(data?.mark?.timeout ?? '', 1, 1440)
                : true,
            });
        }

    }, [data, init]);

    useEffect(() => {
        const t = !_isEqual(data, original);
        setChanges(t);
        if (t) {
            props.onChange(props.name, t, data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, original]);

    useEffect(() => {
        if (props.onUpdateForm) {
            props.onUpdateForm(dataForm);
        }
    }, [dataForm, props]);

    const handleHost = (e, valid) => {
        dataForm.set({ ...dataForm.get(), host: valid });

        const d = { ...data };
        d.dest.host = e.target.value;
        setData(d);
    };

    const handlePort = (e, valid) => {
        dataForm.set({ ...dataForm.get(), port: valid });

        const d = { ...data };
        d.dest.port = e.target.value;
        setData(d);
    };

    const handleProtocol = (e, component) => {
        const d = { ...data };
        d.dest.proto = component.props.value;
        setData(d);
    };

    const handleMark = (e) => {
        const d = { ...data };
        d.mark.enable = e.currentTarget.checked;
        setData(d);

        const form = { ...dataForm.get() };
        if (!e.currentTarget.checked) {
            delete form.range;
        } else {
            form.range = typeChecks.range?.(d.mark.timeout ?? '', 1, 1440);
        }
        dataForm.set(form);
    };

    const handleMarkTimeout = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), range: valid });

        const d = { ...data };
        d.mark.timeout = parseInt(e.target.value);
        setData(d);
    };

    const handleTimeZone = (e, component) => {
        const d = { ...data };
        d.syslog.dst_tz = component.props.value;
        setData(d);
    };

    const handleFacility = (e, component) => {
        const d = { ...data };
        d.syslog.facility = component.props.value;
        setData(d);
    };

    const handleFrameSeparator = (e) => {
        const d = { ...data };
        d.syslog.frame_separator = e.currentTarget.value;
        setData(d);
    };

    const handleRFC = (e) => {
        const d = { ...data };
        d.syslog.rfc = e.currentTarget.value;
        setData(d);
    };

    // const handlePruneTypeChange = (e) => {
    //     const d = { ...data };
    //     d.prune_type = e.target.value;
    //     setData(d);
    // };

    // const handlePruneKeysChange = (e, validID, valid) => {
    //     dataForm.set({...dataForm.get(), [validID]: valid});

    //     const d = { ...data };
    //     d.prune_keys = e.target.value ? e.target.value.replace(/\s/g, '').split(',') : [];
    //     setData(d);
    // };

    const handleSeverity = (e, component) => {
        const d = { ...data };
        d.syslog.severity = component.props.value;
        setData(d);
    };

    const handleCACert = (e) => {
        const d = { ...data };
        d.tls_cert.enable = e.currentTarget.checked;
        setData(d);
    };

    const fileHandler = (file) => {
        const reader = new FileReader();
        return new Promise((resolve, reject) => {
            reader.onload = event => resolve(event.target.result);
            reader.onerror = error => reject(error);
            reader.readAsText(file);
        });
    };

    const handleTLSContents = (e) => {
        fileHandler(e.target.files[0]).then(content => {
            SystemService.exportsCertAttributes(content).then((res) => {
                const d = { ...data };
                d.tls_cert.contents = content;
                d.tls_cert.file_name = `${res.subject} (${res.enddate})`;
                setData(d);
            }, (err) => {
                console.error(err);
            });
        }).catch(err => console.error(err));
    };

    const handleFIPS = (e) => {
        const d = { ...data };
        d.tls_fips = e.currentTarget.checked;
        setData(d);
    };

    const handleSetFormat = (format) => {
        const d = { ...data };

        if (format === 'empty') {
            d.syslog.format = '';
        } else {
            d.syslog.format = systemConstants.export_log_format_templates[format];
        }
        setData(d);
    };

    const handleFormat = (value) => {
        const d = { ...data };
        d.syslog.format = value;
        setData(d);
    };

    const handleAddFormatString = (e) => {
        const d = { ...data };
        const cp = formatRef.getCursor().ch;

        d.syslog.format = [
            data.syslog.format.slice(0, cp),
            e.currentTarget.name,
            data.syslog.format.slice(cp),
        ].join('');

        setData(d);
    };

    const handleUpdateFilter = (filter) => {
        setData({
            ...data,
            filterconf: filter,
        });
    };

    const handleVerify = (k, i, close) => {
        setInit(true);
        props.onVerify(k, i, close);
    };

    const handleCancel = (k, i) => {
        setInit(true);
        setData(original);
        props.onCancel(k, i);
    };

    const handleEventTypeAlerts = (e) => {
        const d = { ...data };
        if (e.currentTarget.checked) {
            d.event_types.push('alert');
        } else {
            d.event_types.splice(d.event_types.indexOf('alert'), 1);
        }
        setData(d);
    };

    const handleEventTypeAnomalies = (e) => {
        const d = { ...data };
        if (e.currentTarget.checked) {
            d.event_types.push('anomaly');
        } else {
            console.log(d.event_types);
            d.event_types.splice(d.event_types.indexOf('anomaly'), 1);
        }
        setData(d);
    };

    const handleEventTypeMetadata = (e) => {
        const d = { ...data };
        if (e.currentTarget.checked) {
            d.event_types.push('bro_log');
        } else {
            d.event_types.splice(d.event_types.indexOf('bro_log'), 1);
        }
        setData(d);
    };

    const handleEventTypeDPIMetadata = (e) => {
        const d = { ...data };
        if (e.currentTarget.checked) {
            d.event_types.push('dpi');
        } else {
            d.event_types.splice(d.event_types.indexOf('dpi'), 1);
        }
        setData(d);
    };

    const handleEventTypeHealthAlerts = (e) => {
        const d = { ...data };
        if (e.currentTarget.checked) {
            d.event_types.push('health_alert');
        } else {
            d.event_types.splice(d.event_types.indexOf('health_alert'), 1);
        }
        setData(d);
    };

    const codemirrorOptions = {
        theme: theme.codeMirror.theme,
        lineWrapping: true,
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Box>
                    <Box display="inline-block" pr={5}>Event Type(s):</Box>
                    <FormControlLabel
                        label="Alerts"
                        onChange={(e) => { handleEventTypeAlerts(e) }}
                        control={<Checkbox disabled={(!props.perms.write || props.policySource === 'mc2')} checked={data.event_types.indexOf('alert') !== -1} />}
                    />
                    {props.cmc && <FormControlLabel
                        label="Anomalies"
                        onChange={(e) => { handleEventTypeAnomalies(e) }}
                        control={<Checkbox disabled={(!props.perms.write || props.policySource === 'mc2')} checked={data.event_types.indexOf('anomaly') !== -1} />}
                    />}
                    <FormControlLabel
                        label="Metadata"
                        onChange={(e) => { handleEventTypeMetadata(e) }}
                        control={<Checkbox disabled={(!props.perms.write || props.policySource === 'mc2')} checked={data.event_types.indexOf('bro_log') !== -1} />}
                    />
                    <FormControlLabel
                        label="DPI Metadata"
                        onChange={(e) => { handleEventTypeDPIMetadata(e) }}
                        control={<Checkbox disabled={(!props.perms.write || props.policySource === 'mc2')} checked={data.event_types.indexOf('dpi') !== -1} />}
                    />
                    <FormControlLabel
                        label="Health Alerts"
                        onChange={(e) => { handleEventTypeHealthAlerts(e) }}
                        control={<Checkbox disabled={(!props.perms.write || props.policySource === 'mc2')} checked={data.event_types.indexOf('health_alert') !== -1} />}
                    />
                </Box>
            </Grid>
            <Grid item container xs={8} spacing={2}>
                <Grid item xs={6}>
                    <Box>
                        <FormControl className={classes.fullWidth}>
                            <FormLabel>Protocol</FormLabel>
                            <Select disabled={(!props.perms.write || props.policySource === 'mc2')} value={data.dest.proto} onChange={handleProtocol}>
                                <MenuItem value="UDP">UDP</MenuItem>
                                <MenuItem value="TCP">TCP</MenuItem>
                                <MenuItem value="TLS">TLS</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <Box mt={3}>
                        <FormControl className={classes.fullWidth}>
                            <FormLabel>Hostname</FormLabel>
                            <Validator
                                disabled={(!props.perms.write || props.policySource === 'mc2')}
                                valueType="host"
                                errorText="Must be a valid host"
                                fullWidth
                                value={data.dest.host}
                                onChange={handleHost}
                            />
                        </FormControl>
                    </Box>
                    <Box mt={3}>
                        <FormControl className={classes.fullWidth}>
                            <FormLabel>Port</FormLabel>
                            <Validator
                                disabled={(!props.perms.write || props.policySource === 'mc2')}
                                valueType="port"
                                errorText="Must be a valid port number"
                                fullWidth
                                value={data.dest.port}
                                onChange={handlePort}
                            />
                        </FormControl>
                    </Box>
                    <Box mt={3}>
                        <FormControl>
                            <FormLabel>RFC</FormLabel>
                            <RadioGroup name="rfc" row value={data.syslog.rfc} onChange={handleRFC}>
                                <FormControlLabel value="new" control={<Radio disabled={(!props.perms.write || props.policySource === 'mc2')} />} label="5424" />
                                <FormControlLabel value="old" control={<Radio disabled={(!props.perms.write || props.policySource === 'mc2')} />} label="3164" />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                    {/* <Box mt={3}>
                        <Typography>Event Pruning Type:</Typography>
                        <RadioGroup row value={data.prune_type} onChange={handlePruneTypeChange}>
                            <FormControlLabel
                                disabled={(!props.perms.write || props.policySource === 'mc2')}
                                label="Inactive"
                                aria-label="inactive"
                                control={<Radio value='inactive' />} />
                            <FormControlLabel
                                disabled={(!props.perms.write || props.policySource === 'mc2')}
                                label="Remove"
                                aria-label="remove"
                                control={<Radio value='black' />} />
                            <FormControlLabel
                                disabled={(!props.perms.write || props.policySource === 'mc2')}
                                label="Add"
                                aria-label="add"
                                control={<Radio value='white' />} />
                        </RadioGroup>
                    </Box>
                    <Box mt={3}>
                        <FormControl className={classes.fullWidth}>
                            <FormLabel>Event Pruning Keys:</FormLabel>
                            <Validator
                                disabled={(!props.perms.write || props.policySource === 'mc2')}
                                allowEmpty
                                valueType="keyPairList"
                                errorText="Must be valid comma separated key pairs ex. bricata:dest_location"
                                fullWidth
                                value={data.prune_keys ? data.prune_keys.join(',') : []}
                                onChange={handlePruneKeysChange}
                            />
                        </FormControl>
                    </Box> */}
                </Grid>
                <Grid item xs={6}>
                    <Box>
                        <FormControl className={classes.fullWidth}>
                            <FormLabel>Facility</FormLabel>
                            <Select disabled={(!props.perms.write || props.policySource === 'mc2')} className={classes.fullWidth} value={data.syslog.facility} onChange={handleFacility}>
                                {Object.keys(systemConstants.facilities).map((v, i) => {
                                    return <MenuItem key={'facility-option-' + i} value={systemConstants.facilities[v]}>{v}</MenuItem>;
                                })}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box mt={3}>
                        <FormControl className={classes.fullWidth}>
                            <FormLabel>Severity</FormLabel>
                            <Select disabled={(!props.perms.write || props.policySource === 'mc2')} className={classes.fullWidth} value={data.syslog.severity} onChange={handleSeverity}>
                                {Object.keys(systemConstants.severities).map((v, i) => {
                                    return <MenuItem key={'severity-option-' + i} value={systemConstants.severities[v]}>{v}</MenuItem>;
                                })}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box mt={3}>
                        <FormControl className={classes.fullWidth}>
                            <FormLabel>Destination Time Zone</FormLabel>
                            <Select disabled={(!props.perms.write || props.policySource === 'mc2')} className={classes.fullWidth} value={data.syslog.dst_tz} onChange={handleTimeZone}>
                                {systemConstants.timezones.map((v, i) => {
                                    return <MenuItem key={'timezone-' + i} value={v.name}>{v.utc_offset} - {v.name}</MenuItem>;
                                })}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box mt={3}>
                        <RadioGroup row>
                            <FormControlLabel
                                disabled={(!props.perms.write || props.policySource === 'mc2')}
                                labelPlacement="start"
                                control={
                                    <Checkbox
                                        checked={data.mark.enable}
                                        name="mark"
                                        onChange={handleMark}
                                    />
                                }
                                label="Mark"
                            />
                            <Box ml={3}>
                                <Validator
                                    valueType="range"
                                    errorText="Timeout must be within 1 and 1440 minutes"
                                    inputProps={{
                                        min: 1,
                                        max: 1440,
                                    }}
                                    endAdornment={<InputAdornment position="end">Minutes</InputAdornment>}
                                    value={data.mark.timeout}
                                    onChange={handleMarkTimeout}
                                    disabled={(!props.perms.write || props.policySource === 'mc2') || !data.mark.enable}
                                />
                            </Box>
                        </RadioGroup>
                    </Box>
                </Grid>
            </Grid>
            <Grid item container xs={4}>
                <Grid item xs={12}>
                    <Box>
                        TCP Frame Separator
                        <RadioGroup
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            row
                            value={data.syslog.frame_separator}
                            onChange={handleFrameSeparator}
                        >
                            <FormControlLabel
                                labelPlacement="start"
                                label="Frame Length"
                                value=""
                                disabled={data.dest.proto === 'UDP'}
                                control={<Radio />}
                            />
                            <FormControlLabel
                                labelPlacement="start"
                                label="New Line"
                                value="'\n'"
                                disabled={data.dest.proto === 'UDP'}
                                control={<Radio />}
                            />
                        </RadioGroup>
                    </Box>
                    <Box mt={3}>
                        <FormControlLabel
                            labelPlacement="start"
                            label="FIPS"
                            disabled={(!props.perms.write || props.policySource === 'mc2') || data.dest.proto !== 'TLS'}
                            control={
                                <Checkbox
                                    checked={data.tls_fips}
                                    name=""
                                    onChange={handleFIPS}
                                />
                            }
                        />
                    </Box>
                    <Box mt={3}>
                        <FormControlLabel
                            labelPlacement="start"
                            label="CA Cert"
                            disabled={(!props.perms.write || props.policySource === 'mc2') || data.dest.proto !== 'TLS'}
                            control={
                                <Checkbox
                                    checked={data.tls_cert.enable}
                                    name=""
                                    onChange={handleCACert}
                                />
                            }
                        />
                    </Box>
                    <Box mt={3}>
                        <input
                            id={'syslog-cert-file-' + props.index}
                            className={classes.hideFileInput}
                            type="file"
                            onChange={handleTLSContents}
                            disabled={(!props.perms.write || props.policySource === 'mc2') || !data.tls_cert.enable}
                        />
                        <label htmlFor={'syslog-cert-file-' + props.index}>
                            <Button
                                variant="contained"
                                color="primary"
                                aria-label="upload cert"
                                component="span"
                                disabled={(!props.perms.write || props.policySource === 'mc2') || !data.tls_cert.enable}
                            >
                                Upload Cert
                            </Button>
                        </label>
                    </Box>
                    <Box className={classes.certBox} display={data.tls_cert.file_name ? 'flex' : 'none'}>
                        {data.tls_cert.file_name}
                    </Box>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Box className={classes.codeContainer} width="100%">
                    <CodeMirror
                        readOnly={!props.perms.write}
                        editorDidMount={(e) => { setFormatRef(e) }}
                        value={data.syslog.format}
                        options={{
                            ...codemirrorOptions,
                            readOnly: (!props.perms.write || props.policySource === 'mc2'),
                        }}
                        onBeforeChange={(c, e, value) => { handleFormat(value) }}
                        lineWrapping={true}
                    />
                </Box>

                {/* <TextField
                    disabled={(!props.perms.write || props.policySource === 'mc2')}
                    label="Format"
                    fullWidth
                    multiline
                    value={data.syslog.format}
                    inputRef={syslogRef}
                    onChange={handleFormat}
                /> */}
                <Box mt={1}>
                    <Accordion>
                        <AccordionSummary>
                            <Box display="flex" justifyContent="flex-end">
                                Format Strings
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                {FORMAT_STRINGS.map((fs, i) => {
                                    return (
                                        <Button
                                            key={fs + '-' + i}
                                            name={fs}
                                            color="primary"
                                            onClick={handleAddFormatString}
                                            style={{ textTransform: 'none' }}
                                        >{fs}</Button>
                                    );
                                })}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Typography>Apply format preset:</Typography>
                <ButtonGroup disabled={(!props.perms.write || props.policySource === 'mc2')}>
                    <Button variant="contained" color="primary" aria-label="CEF" onClick={() => { handleSetFormat('CEF') }}>CEF</Button>
                    <Button variant="contained" color="primary" aria-label="LEEF" onClick={() => { handleSetFormat('LEEF') }}>LEEF</Button>
                    <Button variant="contained" color="primary" aria-label="DPI" onClick={() => { handleSetFormat('DPI') }}>DPI</Button>
                    <Button variant="contained" color="primary" aria-label="Empty" onClick={() => { handleSetFormat('empty') }}>Empty</Button>
                </ButtonGroup>
            </Grid>
            <Filter
                filter={data.filterconf}
                ref={filterRef}
                onUpdate={handleUpdateFilter}
                onVerify={() => { handleVerify(props.name, props.index, false) }}
                policySource={props.policySource}
                perms={props.perms}
            />
            {(props.perms.write || props.policySource !== 'mc2') && <Grid item xs={12}>
                <Box mt={2}>
                    <Button
                        className={classes.buttonRight}
                        variant="contained"
                        color="primary"
                        aria-label="save"
                        onClick={() => { handleVerify(props.name, props.index, true) }}
                        disabled={!checkValidity(dataForm.get())}
                    >Verify</Button>
                    <Button className={classes.buttonRight} variant="contained" color="secondary" aria-label="cancel" onClick={() => { handleCancel(props.name, props.index) }}>Cancel</Button>
                </Box>
            </Grid>}
        </Grid>
    );
});

Syslog.propTypes = {
    data: PropTypes.object.isRequired,
    policySource: PropTypes.string,
    name: PropTypes.string.isRequired,
    index: PropTypes.number.isRequired,
    onCancel: PropTypes.func.isRequired,
    onVerify: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onUpdateForm: PropTypes.func,
    perms: PropTypes.object,
    cmc: PropTypes.bool,
};

export default Syslog;
